import { RawAxiosRequestConfig } from 'axios';
import { action, computed } from 'mobx';
import {
    LoanApplicationBackofficeAPIApiFindAllLoanApplicationsForBackofficeRequest,
    LoanApplicationDto,
} from 'src/generated-api-client';
import { loanApplicationsApi } from 'src/services/apiServices';
import { ListFilter } from 'src/utils/FormHelper';
import { AsyncOperationWithStatus } from 'src/utils/mobx/AsyncOperationWithStatus';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { BasicStoreApi } from 'src/utils/mobx/BasicStore/BasicStore.types';
import { FilterCriteria } from 'src/utils/mobx/FilterCriteria';
import { Pager } from 'src/utils/mobx/Pager';
import { Sorter } from 'src/utils/mobx/Sorter';
import { RequestHelper } from 'src/utils/RequestHelper';

type LoanApplicationsFilter =
    ListFilter<LoanApplicationBackofficeAPIApiFindAllLoanApplicationsForBackofficeRequest>;

export class LoanApplicationsStoreClass extends BasicStore<
    LoanApplicationDto,
    Record<string, any>,
    LoanApplicationDto,
    LoanApplicationDto,
    LoanApplicationDto
> {
    pager = new Pager();
    sorter = new Sorter<LoanApplicationDto>();
    filterCriteria = new FilterCriteria<LoanApplicationsFilter>();

    assignProductLoader = new AsyncOperationWithStatus(
        (applicationId: string, productId: string) => {
            return RequestHelper.unwrapFromAxiosPromise(
                loanApplicationsApi.assignLoanApplicationForBackoffice({
                    id: applicationId,
                    assignLoanApplicationRequest: { productId },
                }),
            );
        },
    );

    declineLoader = new AsyncOperationWithStatus(
        (applicationId: string, reason: string) => {
            return RequestHelper.unwrapFromAxiosPromise(
                loanApplicationsApi.declineLoanApplicationForBackoffice({
                    id: applicationId,
                    finalDeclineLoanApplicationRequest: {
                        reason,
                    },
                }),
            );
        },
    );

    merchantSnapshotsLoader = new AsyncOperationWithStatus((id: string) => {
        return RequestHelper.unwrapFromAxiosPromise(
            loanApplicationsApi.getMerchantSnapshotsForBackoffice({ id }),
        );
    });

    transactionsLoader = new AsyncOperationWithStatus((id: string) => {
        return RequestHelper.unwrapFromAxiosPromise(
            loanApplicationsApi.getTransactionsForBackoffice({ id }),
        );
    });

    repaidLoader = new AsyncOperationWithStatus((id: string) => {
        return RequestHelper.unwrapFromAxiosPromise(
            loanApplicationsApi.repaidLoanApplicationForBackoffice({ id }),
        );
    });

    exportActiveApplicationsLoader = new AsyncOperationWithStatus(
        (options?: RawAxiosRequestConfig) => {
            return RequestHelper.unwrapFromAxiosPromise(
                loanApplicationsApi.exportActiveLoanApplications({
                    ...options,
                    responseType: 'blob',
                }),
            );
        },
    );

    api: BasicStoreApi<
        LoanApplicationDto,
        LoanApplicationDto,
        LoanApplicationDto,
        LoanApplicationDto
    > = {
        loadList: async (...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            const response = await RequestHelper.unwrapFromAxiosPromise(
                loanApplicationsApi.findAllLoanApplicationsForBackoffice(
                    {
                        ...this.filterCriteria.filter,
                        page: this.pager?.page,
                        size: this.pager?.size,
                        sort: this.sorter?.sort,
                        ...this.filterCriteria,
                    },
                    options,
                ),
            );
            this.pager?.setTotal(response.totalElements ?? 0);

            return response.content ?? [];
        },
        loadItem: async (id, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            const response = await RequestHelper.unwrapFromAxiosPromise(
                loanApplicationsApi.getLoanApplicationForBackoffice(
                    { id },
                    options,
                ),
            );

            return response ?? null;
        },
    };

    @action
    assignProduct = (applicationId: string, productId: string) => {
        return this.assignProductLoader.call(applicationId, productId);
    };

    @action
    decline = (applicationId: string, reason: string) => {
        return this.declineLoader.call(applicationId, reason);
    };

    @action
    loadCheckPenaltyData(id: string) {
        return this.merchantSnapshotsLoader.call(id);
    }

    @action
    loadTransactions(id: string) {
        return this.transactionsLoader.call(id);
    }

    @action
    repaid(id: string) {
        return this.repaidLoader.call(id);
    }

    @action exportActiveApplications() {
        return this.exportActiveApplicationsLoader.call() as unknown as Promise<Blob>;
    }

    @computed get merchantSnapshots() {
        return this.merchantSnapshotsLoader.data;
    }

    @computed get transactions() {
        return this.transactionsLoader.data || [];
    }
}

// Shared singleton instance of LoanApplications store
export const LoanApplicationsStore = new LoanApplicationsStoreClass();
